import React, { useId } from "react";
import { startCase, uniq } from "lodash";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";

import Tag from "@components/shared/Tag";
import { formatVolume } from "@utils/formatVolume";
import { formatSchemeDay } from "@utils/schemeDay";
import { Rows } from "./ZoneAccountSummary";

/**
 * One entitlement's details and billing. Always open; mount it only while an
 * entitlement is selected.
 */
const EntitlementDetailDialog: React.FunctionComponent<{
  right: any;
  onClose: () => void;
}> = ({ right, onClose }) => {
  const { t } = useTranslation();
  const billingTitleId = useId();
  // Those its approved subdivisions and amalgamations made, in any period (GET /extractionRights).
  const children: string[] = right.childNames ?? [];
  const date = (value?: string) => (value ? formatSchemeDay(value) : "-");

  const details: [string, React.ReactNode][] = [
    [t("extraction_right.purpose"), right.purpose ?? "-"],
    [t("extraction_right.start_at"), date(right.startAt)],
    [t("extraction_right.end_at"), date(right.endAt)],
    [t("extraction_right.volume"), formatVolume(right.volume)],
    [t("extraction_right.allocation_type"), right.type?.identifier ?? "-"],
    [t("extraction_right.source"), startCase(right.level0Resource?.source)],
    [t("water_class.water_class_header"), right.waterClass?.name ?? "-"],
    ...(children.length
      ? ([
          [t("extraction_right.child_extraction_rights"), children.join(", ")],
        ] as [string, React.ReactNode][])
      : []),
  ];
  const billing: [string, React.ReactNode][] = [
    [t("extraction_right.billing_group"), right.billingGroup?.name ?? "-"],
    [t("extraction_right.freq"), right.subscriber?.billingFrequency ?? "-"],
    [
      t("extraction_right.item_no"),
      uniq(
        (right.waterClass?.waterCharges ?? [])
          .filter(
            (charge: any) => charge.billingGroupId === right.billingGroupId,
          )
          .map((charge: any) => charge.fixedItemNo),
      ).join(", ") || "-",
    ],
  ];

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-gray-500/75" aria-hidden="true" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <Dialog.Panel className="w-full max-w-2xl overflow-hidden rounded-lg bg-white shadow-xl">
            <header className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-3.5">
              <div>
                <Dialog.Title className="flex items-center gap-2 text-base font-bold text-primary-3">
                  {right.name}
                  <EntitlementStatusTag right={right} />
                </Dialog.Title>
                <p className="text-sm text-gray-500">
                  {right.level0Resource?.identifier} ·{" "}
                  {right.level0Resource?.name}
                </p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label={t("common.close") as string}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
              >
                <XMarkIcon className="h-5 w-5" aria-hidden="true" />
              </button>
            </header>

            <div className="grid gap-5 p-5 sm:grid-cols-2">
              <Rows rows={details} />
              <section aria-labelledby={billingTitleId}>
                <h3
                  id={billingTitleId}
                  className="mb-1 text-sm font-bold text-primary-3"
                >
                  {t("extraction_right.billing_info")}
                </h3>
                <Rows rows={billing} />
              </section>
            </div>
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

/** An entitlement's status: active, deleted or inactive. */
export const EntitlementStatusTag: React.FunctionComponent<{ right: any }> = ({
  right,
}) => {
  const { t } = useTranslation();

  return right.isActive ? (
    <Tag status="success">{t("common.active")}</Tag>
  ) : right.deletedAt ? (
    <Tag status="error">{t("common.deleted")}</Tag>
  ) : (
    <Tag status="error">{t("common.inactive")}</Tag>
  );
};

export default EntitlementDetailDialog;
